import axios from "axios";

// Same-origin friendly: if REACT_APP_BACKEND_URL is empty/unset we fall back to
// a relative `/api`. This lets the SAME production build run on any custom
// domain (e.g. https://ptcincorporation.com) without baking a preview/staging
// hostname into the bundle. In preview the env var can point to the preview
// URL (same origin as the frontend) — both forms work.
const RAW = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW.replace(/\/+$/, ""); // strip trailing slash
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

// withCredentials: send/receive the httpOnly auth cookie on every request.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 20000, // 20s — never hang the UI indefinitely on a dead backend
});

// On 401 to a protected admin route (mid-session expiry / invalid cookie),
// notify the app so AuthContext can clear the user and the guard redirects to
// /login. We skip /auth/me (handled by the initial session check) and
// /auth/login (handled inline as "invalid credentials").
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && url.includes("/admin")) {
      window.dispatchEvent(new Event("auth:unauthorized"));
    }
    return Promise.reject(error);
  }
);

// Resolve a media reference (relative /api/media/.. or absolute URL) to a usable src.
// Optionally append on-the-fly transform params (width, format) so the server
// returns an optimized variant. The same URL → same bytes → cacheable by CDN.
export function mediaUrl(ref, opts) {
  if (!ref) return "";
  if (ref.startsWith("http") || ref.startsWith("data:")) return ref;
  let url;
  if (ref.startsWith("/api/")) url = BACKEND_URL ? `${BACKEND_URL}${ref}` : ref;
  else url = ref;
  if (opts && (opts.w || opts.fmt || opts.q)) {
    const params = new URLSearchParams();
    if (opts.w) params.set("w", String(opts.w));
    if (opts.fmt) params.set("fmt", opts.fmt);
    if (opts.q) params.set("q", String(opts.q));
    const sep = url.includes("?") ? "&" : "?";
    url = `${url}${sep}${params.toString()}`;
  }
  return url;
}

/**
 * Generate srcset + base src for an <img> so the browser picks the right size.
 * Pass the original media ref and the maximum displayed width.
 *  - WebP variants for browsers that support it (modern browsers, ~all of them)
 *  - Falls back to JPEG via the same query API
 */
export function responsiveImg(ref, opts = {}) {
  const { maxWidth = 1600, sizes } = opts;
  if (!ref || ref.startsWith("data:")) return { src: mediaUrl(ref), srcSet: undefined, sizes: undefined };
  const widths = [400, 800, 1200, 1600, 2000].filter((w) => w <= maxWidth * 1.5);
  const srcSet = widths.map((w) => `${mediaUrl(ref, { w, fmt: "webp", q: 78 })} ${w}w`).join(", ");
  return {
    src: mediaUrl(ref, { w: maxWidth, fmt: "webp", q: 78 }),
    srcSet,
    sizes: sizes || `(max-width: 768px) 100vw, ${maxWidth}px`,
  };
}

// Resolve a brand asset (logo/favicon) with a cache-busting version derived
// from the unique media id, so a re-uploaded asset never shows stale.
export function brandedSrc(ref) {
  const url = mediaUrl(ref);
  if (!url || url.startsWith("data:")) return url;
  const tag = (String(ref).match(/[0-9a-fA-F-]{6,}/g) || []).pop();
  if (!tag) return url;
  const v = tag.slice(-10);
  return `${url}${url.includes("?") ? "&" : "?"}v=${v}`;
}

export function apiErrorMessage(detail, fallback = "Something went wrong.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return fallback;
}
